import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import CodeBlock from '../components/chat/CodeBlock';
import { virtualClose } from './markdownTier2';

/**
 * ProgressiveMarkdown — render mid-stream markdown without flicker.
 *
 * Wraps react-markdown with three streaming-specific behaviors:
 *
 *   1. virtualClose() patches unfinished code/KaTeX fences before render so
 *      partial code blocks don't oscillate between "plain text" and "code".
 *
 *   2. ```mermaid blocks are rendered as plain <pre><code> while streaming.
 *      The MermaidDiagram renderer only takes over once the persisted
 *      agent_message replaces this bubble — preventing half-parsed diagrams
 *      from blowing up mid-stream.
 *
 *   3. remarkBreaks: a single `\n` becomes <br>. CommonMark-strict (used by
 *      the persisted bubble in ChatMessages) treats `\n` as a space, which
 *      during streaming causes lines that haven't yet received their second
 *      `\n` to render concatenated. Breaks plugin only affects the live
 *      bubble; persisted rendering keeps strict mode.
 *
 * `React.memo`: deltas can fire at 30Hz; skip re-render when text unchanged.
 */
function ProgressiveMarkdown({ text }) {
  const closed = useMemo(() => virtualClose(text || ''), [text]);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      components={{
        // eslint-disable-next-line jsx-a11y/anchor-has-content
        a: ({ node, ...props }) => <a target="_blank" rel="noopener noreferrer" {...props} />,
        code: ({ inline, className, children, ...props }) => {
          if (className === 'language-mermaid' && !inline) {
            return (
              <pre className="mermaid-pending"><code {...props}>{children}</code></pre>
            );
          }
          return (
            <CodeBlock inline={inline} className={className} {...props}>
              {children}
            </CodeBlock>
          );
        },
      }}
    >
      {closed}
    </ReactMarkdown>
  );
}

ProgressiveMarkdown.propTypes = {
  text: PropTypes.string,
};

export default React.memo(ProgressiveMarkdown);
